/*
 * hpq-cart-coupon.js — the unified cart coupon input (cart page + fly-out drawer).
 *
 * The ONLY place a shopper enters a discount code once draft-order checkout is on
 * (the native checkout discount field is hidden). Accepts any code — a Fortune reward
 * or a custom promo. The Fortune footer's "Apply to my cart" dispatches the same
 * hpq:coupon-applied event, so a Fortune code lands here as an applied chip too.
 *
 * State lives in hpq-draft-checkout.js (window.hpqDraftCheckout / hpqAppliedCoupons);
 * this module is the view + the entry point. It validates each code and shows the real
 * money by calling POST {workerBase}/checkout/calculate — the SAME draft path checkout
 * uses — so the summary (coupon discount / shipping / order total, ex-tax) matches what
 * checkout charges. Loaded AFTER hpq-draft-checkout.js so the state module records an
 * applied code before we re-quote.
 */
(function () {
  'use strict';

  var CODE_RE = /^[A-Z0-9-]{1,40}$/;

  function enabled() {
    return (window.HPQ_CONFIG && window.HPQ_CONFIG.draftCheckoutEnabled === true) ||
      window.HPQ_FORCE_DRAFT_CHECKOUT === true;
  }
  function workerBase() { return (window.HPQ_CONFIG && window.HPQ_CONFIG.workerBase) || ''; }

  // The worker derives the customer from this token and IGNORES any customerId in
  // the body — sending one was how any client could price a cart as any customer
  // and confirm whether a discount code was valid for them. A signed-out shopper
  // simply sends no header and is quoted anonymously.
  function caaAuthHeaders(base) {
    var out = {};
    Object.keys(base || {}).forEach(function (k) { out[k] = base[k]; });
    try {
      var api = window.HPQCustomerAccountAPI;
      var t = (api && typeof api.getCachedTokens === 'function') ? api.getCachedTokens() : null;
      if (t && t.accessToken) out['X-CAA-Access-Token'] = t.accessToken;
    } catch (e) { /* never block a quote on a token read */ }
    return out;
  }

  function normalize(s) { return String(s || '').trim().toUpperCase(); }

  function boxes() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-hpq-coupon-box]'));
  }

  function buildItems() {
    var cart = window.hpqCart;
    var raw = (cart && typeof cart.getItems === 'function') ? cart.getItems() : [];
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var vid = String(raw[i].variant_id || '');
      var qty = parseInt(raw[i].quantity, 10);
      if (/^\d+$/.test(vid) && qty >= 1) {
        out.push({ variantId: 'gid://shopify/ProductVariant/' + vid, quantity: qty });
      }
    }
    return out;
  }
  function appliedCoupons() {
    return (window.hpqDraftCheckout && typeof window.hpqDraftCheckout.getCoupons === 'function')
      ? window.hpqDraftCheckout.getCoupons() : [];
  }
  function appliedCodes() {
    return (window.hpqDraftCheckout && typeof window.hpqDraftCheckout.getCodes === 'function')
      ? window.hpqDraftCheckout.getCodes() : [];
  }
  function customerGid() {
    try {
      var api = window.HPQCustomerAccountAPI;
      var c = (api && typeof api.getCachedCustomer === 'function') ? api.getCachedCustomer() : null;
      return (c && c.gid) || '';
    } catch (e) { return ''; }
  }

  var QUOTE_TIMEOUT_MS = 6000; // Abort a hung /checkout/calculate so the quote always settles.

  function quote(items, codes) {
    if (!workerBase()) return Promise.resolve(null);
    // Fortune codes are customer-scoped — the worker needs the customer on the calc for
    // Shopify to apply the code, or a valid reward would look rejected in the cart. It
    // takes that customer from the CAA access token below, never from this body.
    var body = { items: items, appliedCodes: codes };
    // Time-box the request: a worker that accepts the connection but never responds would
    // otherwise leave the quote pending forever (no error surfaced, totals stuck pre-coupon).
    // On abort the fetch rejects → .catch → null, same as any other failure.
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, QUOTE_TIMEOUT_MS) : 0;
    function done(v) { if (timer) { clearTimeout(timer); timer = 0; } return v; }
    var opts = {
      method: 'POST',
      headers: caaAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    };
    if (ctrl) opts.signal = ctrl.signal;
    return fetch(workerBase() + '/checkout/calculate', opts)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return done((j && j.ok && j.serverProjection) ? j.serverProjection : null); })
      .catch(function () { return done(null); });
  }

  // ── UI helpers ─────────────────────────────────────────────────────────
  function showMsg(box, text) {
    var el = box.querySelector('[data-hpq-coupon-msg]');
    if (el) { el.textContent = text; el.hidden = false; }
  }
  function hideMsg(box) {
    var el = box.querySelector('[data-hpq-coupon-msg]');
    if (el) { el.textContent = ''; el.hidden = true; }
  }
  function setBusy(box, on) {
    var btn = box.querySelector('[data-hpq-coupon-apply]');
    var input = box.querySelector('[data-hpq-coupon-input]');
    if (btn) { btn.disabled = on; btn.setAttribute('aria-busy', on ? 'true' : 'false'); }
    if (input) input.disabled = on;
  }
  function clearInput(box) {
    var input = box.querySelector('[data-hpq-coupon-input]');
    if (input) input.value = '';
  }
  // Apply → ✓ → Apply confirmation micro-interaction on a successful apply.
  function flashApplied(box) {
    var btn = box.querySelector('[data-hpq-coupon-apply]');
    if (!btn) return;
    btn.classList.add('hpq-cart-coupon__apply--done');
    btn.innerHTML = '✓';
    btn.setAttribute('aria-label', 'Applied');
    setTimeout(function () {
      if (!btn.isConnected) return; // box re-rendered (drawer) — nothing to revert
      btn.classList.remove('hpq-cart-coupon__apply--done');
      btn.textContent = 'Apply';
      btn.removeAttribute('aria-label');
    }, 1400);
  }

  // ── Quote cache + render ───────────────────────────────────────────────
  var lastQuote = null;
  var lastQuoteSig = null;  // exact (items+codes+customer) signature lastQuote was priced for
  var quoteSeq = 0;
  var quoteCache = {};      // cacheKey(items,codes,customer) -> { q: serverProjection, ts: ms }
  var QUOTE_TTL_MS = 120000; // Re-validate a cached quote after 2 min. Coupon validity is not
                             // immutable — a code can be used up or expire server-side while items,
                             // codes and customer stay identical; without a horizon the cache would
                             // show that stale discount until checkout's 422. 2 min bounds staleness
                             // while still collapsing rapid same-state re-quotes.
  function cachedQuote(key) {
    var e = quoteCache[key];
    if (!e) return null;
    if ((Date.now() - e.ts) > QUOTE_TTL_MS) { delete quoteCache[key]; return null; }
    return e.q;
  }
  var QUOTE_CACHE_MAX = 24; // Bound growth: entries otherwise only clear on a TTL-expired read, so a
                            // long session cycling many item/code/customer combos could accumulate.
  function putQuote(key, q) {
    if (!quoteCache[key] && Object.keys(quoteCache).length >= QUOTE_CACHE_MAX) {
      var keys = Object.keys(quoteCache);
      var oldestKey = keys[0], oldestTs = quoteCache[keys[0]].ts;
      for (var i = 1; i < keys.length; i++) {
        if (quoteCache[keys[i]].ts < oldestTs) { oldestTs = quoteCache[keys[i]].ts; oldestKey = keys[i]; }
      }
      delete quoteCache[oldestKey];
    }
    quoteCache[key] = { q: q, ts: Date.now() };
  }
  var quotePending = false; // a network quote is in flight → the free-ship line shows "Checking…"

  // Stable signature of a cart+codes combination. An unchanged cart (drawer re-opened, a
  // cart:updated that didn't actually change quantities) reuses the priced quote instead of
  // paying another ~1s worker round-trip. variantId:quantity fully determines the automatic
  // (volume) discounts, so a signature match guarantees an identical quote.
  function quoteSig(items, codes) {
    var i = items.map(function (x) { return x.variantId + ':' + x.quantity; }).sort().join('|');
    return i + '#' + codes.slice().sort().join(',');
  }

  // The cache/identity key. Fortune codes are customer-scoped, so the SAME items+codes price
  // differently per customer — the customer MUST be part of the key, or a C1 quote would be served
  // (and published as valid) for C2. cacheKey() is used both as the quoteCache key and, via
  // currentSig(), as the publish gate. currentSig() reads the LIVE cart+codes+customer.
  function cacheKey(items, codes, cid) {
    return quoteSig(items, codes) + '@' + (cid || '');
  }
  function currentSig() {
    return cacheKey(buildItems(), appliedCodes(), customerGid());
  }

  // Publish the authoritative order total + free-ship status so the shipping bars (cart bar,
  // "almost there" nudge, nav announcement) can agree with the coupon summary instead of the
  // pre-coupon cart.total_price. null when no coupon is applied (bars revert to normal).
  function publishQuote() {
    // Only publish a quote that is STILL valid for the live cart+codes+customer. A quote whose
    // signature no longer matches (quantity changed, a code removed, customer switched) is
    // treated as absent → the bars + totals fall back to cart.total_price until an exact quote
    // settles. This closes the phantom-discount window a stale/hung quote would otherwise leave.
    var live = (enabled() && appliedCodes().length && lastQuote && lastQuoteSig === currentSig())
      ? lastQuote : null;
    window.hpqCouponQuote = live;
    document.dispatchEvent(new CustomEvent('hpq:quote-updated', { detail: { quote: window.hpqCouponQuote } }));
  }

  function refreshQuote() {
    var items = buildItems();
    var codes = appliedCodes();
    if (!enabled() || !codes.length || !items.length) {
      // Bump the sequence so ANY in-flight quote fired before this (e.g. the coupon that was
      // just removed) fails its staleness check on resolve and can't repopulate lastQuote.
      ++quoteSeq;
      lastQuote = null;
      lastQuoteSig = null;
      quotePending = false;
      renderAll();
      publishQuote();
      return Promise.resolve(null);
    }
    var cid = customerGid();
    var key = cacheKey(items, codes, cid);
    var cached = cachedQuote(key);
    if (cached) {
      // Instant: this exact cart+codes+customer was priced within the TTL. No network, no flash.
      // Advance the sequence so any OLDER in-flight request can't resolve on top of this cached
      // result and clobber it (its seq check will now fail).
      ++quoteSeq;
      lastQuote = cached;
      lastQuoteSig = key;
      quotePending = false;
      renderAll();
      publishQuote();
      return Promise.resolve(lastQuote);
    }
    // Network path — the signature just changed, so the previously published quote is now stale.
    // Invalidate it immediately (bars fall back to cart.total_price) and mark the free-ship line
    // "Checking…" (via hpq:quote-pending) until the quote lands, so they never show the pre-coupon
    // claim and then snap to the real number. (Duplicate same-signature requests from the double
    // cart:updated per mutation are collapsed upstream by the cart:updated debounce, so no
    // in-flight dedup is needed here — which also avoids a dedup that could leave a stale published
    // quote for the live cart.)
    lastQuote = null;
    lastQuoteSig = null;
    window.hpqCouponQuote = null;
    quotePending = true;
    document.dispatchEvent(new CustomEvent('hpq:quote-pending'));
    renderAll();
    var seq = ++quoteSeq;
    return quote(items, codes).then(function (q) {
      if (seq !== quoteSeq) return lastQuote; // a newer request superseded this one
      quotePending = false;
      lastQuote = q;
      lastQuoteSig = q ? key : null;
      // Cache CLEAN projections only. Caching a rejected projection (e.g. an anonymous quote during
      // login) would let the cache-hit path replay the rejection without re-evaluating it — leaving
      // a dead code applied after logout, or skipping a needed detach once identity is known. A
      // rejected combo therefore always re-quotes, so the guard below runs against live identity.
      if (q && !(q.rejectedCodes && q.rejectedCodes.length)) putQuote(key, q);
      // Shopify refused a stored code (e.g. it expired while sitting in the cart) — detach it
      // so checkout can't send a dead code, and tell the shopper via the box message.
      // Only auto-detach a "rejected" code when we actually hold a customer identity. Login has
      // several transient states — pre-token (PKCE / silent-auth in flight), tokens-present-but-
      // customer-not-loaded, and failed/logged-out — and in ALL of them a customer-scoped Fortune
      // reward prices anonymously and comes back rejected. Stripping it then would silently discard
      // a valid earned discount before 'customer_loaded' can re-price it. A genuinely expired code
      // still detaches once logged in (gid present) and, worst case, is caught by the
      // /checkout/create-draft 422 handler. Rejected projections are never cached (see below), so
      // this re-evaluates against live identity on every quote.
      if (q && q.rejectedCodes && q.rejectedCodes.length && customerGid()) {
        // Map each worker-echoed rejected code back to the ACTUAL applied-code string. Codes can
        // differ in case between producers (e.g. 'fortune-1' applied vs 'FORTUNE-1' echoed), and
        // removeCoupon() compares case-sensitively — dispatching the echoed casing would remove
        // nothing, and the removal event immediately re-quotes → re-rejects → an infinite
        // worker-hammering loop (rejected quotes are no longer cached to break it). Remove only
        // codes actually present, using their applied casing; ignore unknown echoes.
        var applied = appliedCodes();
        var removed = [];
        q.rejectedCodes.forEach(function (rc) {
          var norm = normalize(rc);
          for (var ai = 0; ai < applied.length; ai++) {
            if (normalize(applied[ai]) === norm && removed.indexOf(applied[ai]) === -1) {
              removed.push(applied[ai]);
              break;
            }
          }
        });
        removed.forEach(function (code) {
          document.dispatchEvent(new CustomEvent('hpq:coupon-removed', { detail: { code: code } }));
        });
        if (removed.length) {
          boxes().forEach(function (box) {
            showMsg(box, removed.join(', ') + ' could no longer be applied and was removed.');
          });
        }
      }
      renderAll();
      publishQuote();
      return q;
    });
  }

  function renderAll() {
    var coupons = appliedCoupons();
    boxes().forEach(function (box) {
      box.hidden = !enabled();
      renderChips(box, coupons);
      // The old summary block is retired — the coupon now lives in the cart's own totals.
      var sum = box.querySelector('[data-hpq-coupon-summary]');
      if (sum) { sum.hidden = true; sum.innerHTML = ''; }
    });
  }

  function renderChips(box, coupons) {
    var ul = box.querySelector('[data-hpq-coupon-chips]');
    if (!ul) return;
    ul.innerHTML = '';
    coupons.forEach(function (c) {
      var li = document.createElement('li');
      li.className = 'hpq-cart-coupon__chip';

      var label = document.createElement('span');
      label.className = 'hpq-cart-coupon__chip-code';
      label.textContent = c.code + (c.pct ? '  −' + c.pct + '%' : '');

      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'hpq-cart-coupon__chip-remove';
      rm.setAttribute('aria-label', 'Remove ' + c.code);
      rm.textContent = '×';
      rm.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('hpq:coupon-removed', { detail: { code: c.code } }));
      });

      li.appendChild(label);
      li.appendChild(rm);
      ul.appendChild(li);
    });
  }

  // ── Apply ──────────────────────────────────────────────────────────────
  function applyFromBox(box) {
    var input = box.querySelector('[data-hpq-coupon-input]');
    var code = normalize(input ? input.value : '');
    hideMsg(box);

    if (!code) { showMsg(box, 'Enter a promo code.'); return; }
    if (!CODE_RE.test(code)) { showMsg(box, 'That code doesn’t look right.'); return; }
    if (appliedCodes().indexOf(code) !== -1) { showMsg(box, 'That code is already applied.'); clearInput(box); return; }
    if (!buildItems().length) { showMsg(box, 'Add something to your cart first, then apply.'); return; }

    setBusy(box, true);
    // Validate + price the candidate against Shopify (the same draft path checkout uses)
    // before committing it to the applied set. Only a code Shopify actually honors is added.
    var candidate = appliedCodes().concat([code]);
    quote(buildItems(), candidate).then(function (q) {
      setBusy(box, false);
      if (!q) { showMsg(box, 'Couldn’t check that code just now — try again.'); return; }
      // Case-insensitive acceptance: the worker may echo canonical casing that differs from the
      // typed input (the detach path already normalizes for the same reason). A strict indexOf
      // would reject a code Shopify actually honors.
      var accepted = false;
      var acc = q.acceptedCodes || [];
      for (var qi = 0; qi < acc.length; qi++) {
        if (normalize(acc[qi]) === normalize(code)) { accepted = true; break; }
      }
      if (!accepted) {
        showMsg(box, 'That code can’t be applied — it may be expired, already used, or not combinable.');
        return;
      }
      clearInput(box);
      hideMsg(box);
      flashApplied(box);
      // Commit to shared state; the applied listener re-quotes + re-renders every box.
      document.dispatchEvent(new CustomEvent('hpq:coupon-applied', {
        detail: { code: code, pct: 0, exp: 0, customerId: customerGid() }
      }));
    });
  }

  // ── Wiring ─────────────────────────────────────────────────────────────
  // No <form> here (it would be a nested form inside the cart form and get stripped), so bind
  // the button click + Enter-in-input directly. Guard on the box node so a drawer re-render
  // rebinds fresh nodes but the same node is never double-bound.
  function bind(box) {
    if (box.__hpqCouponBound) return;
    box.__hpqCouponBound = true;
    var applyBtn = box.querySelector('[data-hpq-coupon-apply]');
    var input = box.querySelector('[data-hpq-coupon-input]');
    if (applyBtn) applyBtn.addEventListener('click', function (e) { e.preventDefault(); applyFromBox(box); });
    if (input) input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); applyFromBox(box); }
    });
  }

  function init() {
    boxes().forEach(bind);
    renderAll();
    refreshQuote();
  }

  // Every cart mutation fires TWO cart:updated in the same tick (local-cart's broadcast + the
  // drawer's optimistic dispatch). Coalesce them into a single init()/refreshQuote on the next
  // frame so we don't fire two identical network quotes per change (the second would waste a
  // worker round-trip and, if it hung, could strand a valid quote). Different-tick changes still
  // re-quote independently. Applied/removed/auth run init() directly — those need an immediate
  // re-price, and they aren't the double-fire source.
  var reinitScheduled = false;
  var raf = (typeof window.requestAnimationFrame === 'function')
    ? window.requestAnimationFrame
    : function (cb) { return setTimeout(cb, 0); };
  function scheduleReinit() {
    if (reinitScheduled) return;
    reinitScheduled = true;
    raf(function () { reinitScheduled = false; init(); });
  }

  // Applied/removed from ANY source (this box, the Fortune footer, empty-cart reset) →
  // re-render chips and re-price.
  document.addEventListener('hpq:coupon-applied', function () { init(); });
  document.addEventListener('hpq:coupon-removed', function () { init(); });
  // Customer identity changed (login/logout/token refresh) → Fortune codes are customer-scoped, so
  // any published quote priced for the old customer must be re-validated (or cleared) immediately.
  document.addEventListener('hpq:auth-state-changed', function () { init(); });
  // cart:updated (quantity change, drawer re-render, PDP add) → debounced re-price.
  document.addEventListener('cart:updated', scheduleReinit);

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
